import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  type AccountState,
  type LineOAuthAuthorizeData,
} from "../lib/api";
import { resolveLineOAuthCallbackUrl, resolveLineOAuthPopupCompleteUrl } from "../lib/config";
import { AppError, logError } from "../lib/errors";
import {
  clearLineOAuthAttempt,
  clearLineOAuthPending,
  clearLineOAuthPopupSnapshot,
  clearLineOAuthTokenSnapshot,
  isLineOAuthPopupMessage,
  readLineOAuthPending,
  readLineOAuthPopupSnapshot,
  readLineOAuthTokenSnapshot,
  writeLineOAuthAttempt,
  writeLineOAuthContextSnapshot,
  writeLineOAuthTokenSnapshot,
} from "../lib/lineOAuthPopup";
import { storage } from "../lib/storage";
import {
  getLineLinkStatusDescription,
  getLineLinkStatusLabel,
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromAccountState,
  type LineLinkSnapshot,
  writeLineLinkSnapshot,
} from "../lib/lineLink";
import { logoutToSplash } from "../lib/logout";
import { getLineStatusBadgeClass, UI_TEXT_TONE } from "../lib/uiTone";

type LineLinkCardProps = {
  returnTo: string;
  returnState?: unknown;
};

const LINE_OAUTH_POPUP_NAME = "wizmate-line-oauth";
const LINE_OAUTH_STORAGE_KEY = "wizmate.line-oauth-complete";
const LINE_LINK_COMPLETION_ERROR_MESSAGE =
  "LINE連携を完了できませんでした。時間をおいてもう一度お試しください。\nすでに別のWizMateアカウントにLINEが連携されている可能性がある場合は、お問い合わせください。";

function isPopupOpen(popup: Window | null) {
  if (!popup) return false;

  try {
    return popup.closed === false;
  } catch {
    return false;
  }
}

function isMobileUserAgent() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function formatLinkedAt(value?: string) {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function openCenteredPopup(url = "about:blank") {
  const width = 500;
  const height = 600;
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
  const left = Math.max(0, Math.round(viewportWidth / 2 - width / 2 + dualScreenLeft));
  const top = Math.max(0, Math.round(viewportHeight / 2 - height / 2 + dualScreenTop));

  return window.open(
    url,
    LINE_OAUTH_POPUP_NAME,
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`,
  );
}

function toGenericErrorMessage() {
  return LINE_LINK_COMPLETION_ERROR_MESSAGE;
}

function getConnectErrorMessage(error: unknown) {
  if (error instanceof AppError && error.code === "INVALID_RESPONSE") {
    return LINE_LINK_COMPLETION_ERROR_MESSAGE;
  }

  return toGenericErrorMessage();
}

function getDisconnectErrorMessage(error: unknown) {
  if (error instanceof AppError && error.code === "NOT_FOUND") {
    return "LINE連携の状態が見つかりませんでした。ページを開き直してもう一度お試しください。";
  }

  return toGenericErrorMessage();
}

function getCompletionErrorMessage(errorCode: string | null) {
  switch (errorCode) {
    case "access_denied":
      return "LINEログインがキャンセルされました。";
    case "not_linked":
      return LINE_LINK_COMPLETION_ERROR_MESSAGE;
    case "not_friend":
      return "WizMate公式LINEアカウントを友だち追加、またはブロックを解除してから連携してください。";
    case "callback_failed":
      return LINE_LINK_COMPLETION_ERROR_MESSAGE;
    case "line_already_linked":
      return LINE_LINK_COMPLETION_ERROR_MESSAGE;
    default:
      return LINE_LINK_COMPLETION_ERROR_MESSAGE;
  }
}

export function LineLinkCard({ returnTo, returnState }: LineLinkCardProps) {
  const popupRef = useRef<Window | null>(null);
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());
  const [statusLoading, setStatusLoading] = useState(false);
  const [connectSubmitting, setConnectSubmitting] = useState(false);
  const [disconnectSubmitting, setDisconnectSubmitting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectConflict, setConnectConflict] = useState(false);
  const [connectSuccess, setConnectSuccess] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnectSuccess, setDisconnectSuccess] = useState<string | null>(null);

  const statusLabel = useMemo(() => getLineLinkStatusLabel(lineLinkSnapshot), [lineLinkSnapshot]);
  const statusDescription = useMemo(
    () => getLineLinkStatusDescription(lineLinkSnapshot),
    [lineLinkSnapshot],
  );
  const linkedAtLabel = useMemo(() => formatLinkedAt(lineLinkSnapshot.linkedAt), [lineLinkSnapshot.linkedAt]);
  const isLinked = lineLinkSnapshot.status === "linked";
  const isLineSsoUser = useMemo(() => {
    const { authProvider } = storage.getAuth();
    return authProvider === "line";
  }, []);

  const applyAccountState = useCallback((accountState: AccountState) => {
    const nextSnapshot = resolveLineLinkSnapshotFromAccountState(accountState, readLineLinkSnapshot());
    setLineLinkSnapshot(nextSnapshot);
    writeLineLinkSnapshot(nextSnapshot);
    return nextSnapshot;
  }, []);

  const loadAccountLineStatus = useCallback(async () => {
    const accountState = await api.getMe();
    return applyAccountState(accountState);
  }, [applyAccountState]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);

    try {
      await loadAccountLineStatus();
    } catch (error) {
      logError(error);
      setStatusError(LINE_LINK_COMPLETION_ERROR_MESSAGE);
      setLineLinkSnapshot(readLineLinkSnapshot());
    } finally {
      setStatusLoading(false);
    }
  }, [loadAccountLineStatus]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const consumeCompletion = useCallback(
    async (errorCode: string | null, lineConnect: "success" | "error") => {
      clearLineOAuthPopupSnapshot();
      clearLineOAuthPending();
      clearLineOAuthAttempt();
      clearLineOAuthTokenSnapshot();
      setConnectSubmitting(false);

      if (popupRef.current && popupRef.current.closed === false) {
        try {
          popupRef.current.close();
        } catch {
          // noop
        }
        popupRef.current = null;
      } else {
        popupRef.current = null;
      }

      if (lineConnect === "error") {
        setConnectError(getCompletionErrorMessage(errorCode));
        return;
      }

      try {
        const nextSnapshot = await loadAccountLineStatus();
        if (nextSnapshot.status !== "linked") {
          setConnectError(getCompletionErrorMessage("not_linked"));
          return;
        }

        setConnectError(null);
        setConnectSuccess("LINE連携が完了しました。");
      } catch (error) {
        logError(error);
        setConnectError(getCompletionErrorMessage("callback_failed"));
      }
    },
    [loadAccountLineStatus],
  );

  const consumePendingOAuth = useCallback(async () => {
    const pending = readLineOAuthPending();
    if (!pending) return false;
    clearLineOAuthPending();

    setConnectSubmitting(true);
    setConnectError(null);
    setConnectConflict(false);
    setConnectSuccess(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);

    if (pending.type === "error") {
      clearLineOAuthAttempt();
      setConnectSubmitting(false);
      setConnectConflict(false);
      setConnectError(getCompletionErrorMessage(pending.error));
      return true;
    }

    try {
      const savedToken = readLineOAuthTokenSnapshot();
      if (savedToken) {
        clearLineOAuthTokenSnapshot();
        storage.setAuth({ accessToken: savedToken });
      }

      await api.completeLineOAuth({
        code: pending.code,
        state: pending.state,
        redirectUri: pending.redirectUri ?? resolveLineOAuthCallbackUrl(),
      });
      clearLineOAuthAttempt();

      const nextSnapshot = await loadAccountLineStatus();
      setConnectSubmitting(false);
      if (nextSnapshot.status !== "linked") {
        setConnectError(getCompletionErrorMessage("not_linked"));
        return true;
      }
      setConnectSuccess("LINE連携が完了しました。");
    } catch (error) {
      clearLineOAuthAttempt();
      logError(error);
      setConnectSubmitting(false);
      if (error instanceof AppError && error.code === "FORBIDDEN") {
        setConnectConflict(false);
        setConnectError(getCompletionErrorMessage("not_friend"));
      } else if (error instanceof AppError && error.code === "CONFLICT") {
        setConnectConflict(true);
        setConnectError(getCompletionErrorMessage("line_already_linked"));
      } else {
        setConnectConflict(false);
        setConnectError(getCompletionErrorMessage("callback_failed"));
      }
    }
    return true;
  }, [loadAccountLineStatus]);

  useEffect(() => {
    void consumePendingOAuth();
  }, [consumePendingOAuth]);

  const consumeStoredSnapshot = useCallback(async () => {
    const snapshot = readLineOAuthPopupSnapshot();
    if (!snapshot) return false;
    await consumeCompletion(snapshot.errorCode, snapshot.lineConnect);
    return true;
  }, [consumeCompletion]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isLineOAuthPopupMessage(event.data)) return;
      void consumeCompletion(event.data.errorCode, event.data.lineConnect);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LINE_OAUTH_STORAGE_KEY || !event.newValue) return;
      void consumeStoredSnapshot();
    };

    const handleFocus = () => {
      void consumeStoredSnapshot();
      if (isPopupOpen(popupRef.current)) {
        return;
      }
      void consumePendingOAuth();
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("pageshow", handleFocus);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pageshow", handleFocus);
    };
  }, [consumeCompletion, consumePendingOAuth, consumeStoredSnapshot]);

  useEffect(() => {
    if (!connectSubmitting) return undefined;

    const intervalId = window.setInterval(() => {
      const popup = popupRef.current;
      if (!popup) return;

      let isClosed = false;
      try {
        isClosed = popup.closed;
      } catch {
        isClosed = true;
      }

      if (!isClosed) return;

      popupRef.current = null;
      void (async () => {
        const consumedSnapshot = await consumeStoredSnapshot();
        const consumedPending = await consumePendingOAuth();
        if (!consumedSnapshot && !consumedPending) {
          setConnectSubmitting(false);
          setConnectError(getCompletionErrorMessage("callback_failed"));
        }
      })();
    }, 500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [connectSubmitting, consumePendingOAuth, consumeStoredSnapshot]);

  const handleConnect = async () => {
    clearLineOAuthPending();
    clearLineOAuthAttempt();
    clearLineOAuthPopupSnapshot();
    clearLineOAuthTokenSnapshot();

    setConnectSubmitting(true);
    setConnectError(null);
    setConnectConflict(false);
    setConnectSuccess(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);

    let popup: Window | null = null;

    try {
      writeLineOAuthContextSnapshot({ returnTo, returnState });

      const isMobile = isMobileUserAgent();
      const redirectUri = isMobile ? resolveLineOAuthCallbackUrl() : resolveLineOAuthPopupCompleteUrl();
      if (!isMobile) {
        popup = openCenteredPopup();
      }

      const response: LineOAuthAuthorizeData = await api.startLineOAuth({ redirectUri });
      writeLineOAuthAttempt({ state: response.state, redirectUri });

      if (isMobile) {
        const { accessToken } = storage.getAuth();
        if (accessToken) writeLineOAuthTokenSnapshot(accessToken);
        window.location.assign(response.authorizeUrl);
        return;
      }

      if (popup) {
        popup.location.href = response.authorizeUrl;
        popupRef.current = popup;
        return;
      }

      window.location.assign(response.authorizeUrl);
    } catch (error) {
      clearLineOAuthPending();
      clearLineOAuthAttempt();
      clearLineOAuthTokenSnapshot();
      if (popup && !popup.closed) {
        try {
          popup.close();
        } catch {
          // noop
        }
      }
      logError(error);
      setConnectSubmitting(false);
      setConnectError(getConnectErrorMessage(error));
    }
  };

  const handleDisconnect = async () => {
    setDisconnectSubmitting(true);
    setDisconnectError(null);
    setDisconnectSuccess(null);
    setConnectError(null);
    setConnectSuccess(null);

    try {
      await api.disconnectLineLink();
      await loadAccountLineStatus();
      setDisconnectConfirmOpen(false);
      setDisconnectSuccess("LINE連携を解除しました。");
    } catch (error) {
      logError(error);
      setDisconnectError(getDisconnectErrorMessage(error));
    } finally {
      setDisconnectSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className={`max-w-md text-sm leading-6 ${UI_TEXT_TONE.body}`}>
          LINEログインで認証し、必要に応じて公式アカウントの友だち追加までまとめて進めます。
        </p>

        <div className="flex flex-col gap-2 sm:items-end">
          {!isLinked ? (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connectSubmitting || disconnectSubmitting}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#5F8B57] px-5 py-3 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {connectSubmitting ? "LINEへ移動中..." : "LINE連携を開始する"}
            </button>
          ) : null}

          {isLinked ? (
            <>
              <button
                type="button"
                onClick={() => setDisconnectConfirmOpen(true)}
                disabled={statusLoading || disconnectSubmitting || isLineSsoUser}
                className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2 text-xs font-bold text-[#4A604B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {disconnectSubmitting ? "解除中..." : "連携を解除"}
              </button>
              {isLineSsoUser ? (
                <p className="text-right text-xs text-[#8B9E8C]">LINEログインで登録されたため解除できません。</p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <div className="mt-4 rounded-[1.8rem] border border-[#D8E7D3] bg-[#F7FBF3] px-5 py-5 shadow-[0_10px_24px_rgba(95,139,87,0.08)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className={`text-[12px] font-bold tracking-[0.14em] ${UI_TEXT_TONE.eyebrow}`}>現在の連携状態</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full border px-3 py-1 text-xs font-black ${getLineStatusBadgeClass(lineLinkSnapshot)}`}
              >
                {statusLoading ? "確認中..." : statusLabel}
              </span>
              {lineLinkSnapshot.lineDisplayName ? (
                <span className="text-xs font-bold text-[#6F8B63]">{lineLinkSnapshot.lineDisplayName}</span>
              ) : null}
            </div>
            <p className={`mt-3 text-sm leading-6 ${UI_TEXT_TONE.sectionTitle}`}>{statusDescription}</p>
            {linkedAtLabel ? (
              <p className={`mt-2 text-xs ${UI_TEXT_TONE.meta}`}>連携日時: {linkedAtLabel}</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => void loadStatus()}
            disabled={statusLoading || connectSubmitting || disconnectSubmitting}
            className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2.5 text-sm font-bold text-[#4A604B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {statusLoading ? "確認中..." : "状態を更新"}
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-[1.5rem] border border-white/70 bg-white/80 p-4 text-sm leading-6 ${UI_TEXT_TONE.sectionTitle}`}>
        <p>1. 「LINE連携を開始する」からLINEの認証画面を開きます。</p>
        <p>2. 認証後、そのまま公式アカウントの友だち追加まで進めます。</p>
        <p>3. 完了するとこの画面に戻り、連携状態が更新されます。</p>
      </div>

      {statusError ? (
        <div className="mt-4 whitespace-pre-line rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {statusError}
        </div>
      ) : null}
      {connectSuccess ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-3 text-sm text-[#58705A]">
          {connectSuccess}
        </div>
      ) : null}
      {connectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          <p className="whitespace-pre-line">{connectError}</p>
          {!connectConflict && !isLinked ? (
            <button
              type="button"
              onClick={() => void handleConnect()}
              disabled={connectSubmitting || disconnectSubmitting}
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[#5F8B57] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {connectSubmitting ? "LINEへ移動中..." : "もう一度LINE連携を試す"}
            </button>
          ) : null}
          {connectConflict ? (
            <div className="mt-3 flex flex-col gap-2">
              <p className="text-xs leading-5 text-[#A1493E]">
                以前に別の方法で登録している場合は、いったんログアウトして、登録時に使った方法でログインし直してください。
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={logoutToSplash}
                  className="inline-flex items-center justify-center rounded-full bg-[#A1493E] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#8E3E35]"
                >
                  ログアウトしてやり直す
                </button>
                <button
                  type="button"
                  onClick={logoutToSplash}
                  className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2.5 text-sm font-bold text-[#4A604B] transition hover:bg-[#F8FCF5]"
                >
                  別のログイン方法でログインする
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {disconnectSuccess ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-3 text-sm text-[#58705A]">
          {disconnectSuccess}
        </div>
      ) : null}
      {disconnectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {disconnectError}
        </div>
      ) : null}

      {disconnectConfirmOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center overflow-x-hidden bg-[#2B211D]/45 px-4 sm:px-6">
          <div className="w-full max-w-sm rounded-[2rem] border border-[#D8E7D3] bg-white p-6 shadow-[0_24px_60px_rgba(62,85,64,0.18)]">
            <p className={`text-[12px] font-black uppercase tracking-[0.18em] ${UI_TEXT_TONE.eyebrow}`}>確認</p>
            <h3 className={`mt-2 text-xl font-black ${UI_TEXT_TONE.sectionTitle}`}>LINE連携を解除しますか？</h3>
            <p className={`mt-3 text-sm leading-6 ${UI_TEXT_TONE.bodyMuted}`}>
              解除すると、LINEでの通知や配信は使えなくなります。再利用するには、あらためてLINE連携が必要です。
            </p>
            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setDisconnectConfirmOpen(false)}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full border border-[#C8D8C2] bg-white px-4 py-3 text-sm font-bold text-[#6A4F44] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full bg-[#A1493E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#8E3E35] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {disconnectSubmitting ? "解除中..." : "解除する"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
